import Spinner from '@creator/ui/components/Spinner/Spinner';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import { LinkMetaPreview } from '@src/basic-components/LinkMetaPreview/LinkMetaPreview';
import useDidUpdateEffect from '@src/hooks/use-did-update-effect';
import useLinkMeta from '@src/hooks/use-link-meta';
import type { LinkMeta } from '@src/types/link-meta';
import { noop } from '@src/utils/utils';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface LinkMetaFetcherProps {
    className?: string;
    url: string;
    initialLinkMeta?: LinkMeta;
    onSuccess: (linkMeta: LinkMeta) => void;
    onError: (error: Error) => void;
    onDelete: () => void;
}

const LinkMetaFetcher: FC<LinkMetaFetcherProps> = props => {
    const { url, className, onSuccess, onError, initialLinkMeta } = props;

    const { linkMeta, status, fetchLinkMeta, resetLinkMeta } = useLinkMeta({ initialLinkMeta, onSuccess, onError });

    const { t } = useTranslation();

    useEffect(() => {
        if (!url || linkMeta) return;

        fetchLinkMeta(url);
    }, [url]);

    useDidUpdateEffect(() => {
        if (!url) return resetLinkMeta();
    }, [url]);

    function onDeleteClick() {
        const { onDelete = noop } = props;

        resetLinkMeta();
        onDelete();
    }

    function renderError() {
        return <Text className="text-sm text-gray-500">{t('commentInput.linkMetaFetcher.errorFetchingLinkMeta')}</Text>;
    }

    function renderLoading() {
        return (
            <div className="flex items-center space-x-1">
                <Text className="text-sm text-gray-500">{t('commentInput.linkMetaFetcher.fetchingLinkMeta')}</Text>
                <Spinner className="w-4 h-4" />
            </div>
        );
    }

    function renderContent() {
        // Show loading message when metadata is being fetched
        if (status === 'error') return renderError();
        if (status === 'pending') return renderLoading();
        if (!linkMeta) return;

        return <LinkMetaPreview isEditMode={true} linkMeta={linkMeta} onDelete={onDeleteClick} />;
    }

    return <div className={cn('', className)}>{renderContent()}</div>;
};

export default LinkMetaFetcher;
